// ---------------------------------------------------------------
// Subtle UI click sound — synthesized, no audio files needed
// ---------------------------------------------------------------

let ctx = null;

// UI-sound mute (alert sounds are separate and never muted)
const MUTE_KEY = 'dd-ui-muted';
let muted = localStorage.getItem(MUTE_KEY) === '1';

export const isUiMuted = () => muted;
export function setUiMuted(v) {
  muted = !!v;
  localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
}

// Shared, unlocked AudioContext for the whole app. iOS WKWebView creates every
// new AudioContext in a "suspended" state that only resumes inside a user gesture,
// so alert sounds that spin up their own context stay silent. Everything routes
// through this one instead.
export function getSharedAudioCtx() {
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  } catch { return null; }
}

// Unlock audio (and warm up speech) on the first user gestures — required on iOS
// so later alert chimes/voice actually make sound. Idempotent; safe to leave bound.
let unlockBound = false;
export function armAudioUnlock() {
  if (unlockBound || typeof document === 'undefined') return;
  unlockBound = true;
  const unlock = () => {
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume();
      if (window.speechSynthesis && window.speechSynthesis.getVoices) window.speechSynthesis.getVoices();
    } catch { /* ignore */ }
  };
  ['pointerdown', 'touchend', 'click'].forEach((ev) => document.addEventListener(ev, unlock, { passive: true }));
}

function tick() {
  if (muted) return;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    // a short, soft "tap": quick pitch drop with a fast fade-out
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1400, t);
    osc.frequency.exponentialRampToValueAtTime(700, t + 0.03);

    filter.type = 'lowpass';
    filter.frequency.value = 3200;

    gain.gain.setValueAtTime(0.18, t); // clearly audible, still soft
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);

    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.06);
  } catch {
    // audio unavailable — stay silent
  }
}

// ---- whoosh for opening/closing menus & panels ----
function whoosh(up) {
  if (muted) return;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    const dur = 0.28;

    // soft white-noise burst
    const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;

    // sweep the filter up for "open", down for "close"
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.2;
    filter.frequency.setValueAtTime(up ? 350 : 2400, t);
    filter.frequency.exponentialRampToValueAtTime(up ? 2400 : 350, t + dur);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.14, t + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    src.connect(filter).connect(gain).connect(ctx.destination);
    src.start(t);
    src.stop(t + dur);
  } catch { /* silent */ }
}

export const playOpen = () => whoosh(true);   // rising "suush"
export const playClose = () => whoosh(false); // falling "suosh"

export function enableClickSound() {
  const handler = (e) => {
    // only real user clicks (not programmatic), and only primary button
    if (!e.isTrusted || e.button !== 0) return;
    // stay silent inside the map (panning, zooming, tapping around)
    if (e.target.closest && e.target.closest('.leaflet-container')) return;
    tick();
  };
  document.addEventListener('pointerdown', handler);
  return () => document.removeEventListener('pointerdown', handler);
}
